import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import AbaProtegida from '../components/common/AbaProtegida';
import { ConfiguracoesService, Configuracao, Conferente } from '../services/configuracoesService';
import { AvisosService, Aviso } from '../services/avisosService';
import MotivosOcorrenciaService, { MotivoOcorrencia } from '../services/motivosOcorrenciaServiceAPI';
import { usePermissoes } from '../hooks/usePermissoes';
import {
  motivosTransferenciaService,
  motivosCancelamentoService,
  motivosReprovacaoService,
  motivosReagendamentoService,
  motivosFinalizacaoService,
  avaliacaoConfigService,
  posVisitaConfigService
} from '../services/visitasConfigService';

const Configuracoes: React.FC = () => {
  const { user } = useAuth();
  const { filialAtual, setFilialAtual } = useApp();
  const { temPermissao: podeGerenciarNotificacoes } = usePermissoes('configuracoes', 'gerenciar_notificacoes');
  const { temPermissao: podeGerenciarTermosRegras } = usePermissoes('configuracoes', 'gerenciar_termos_regras');
  const { temPermissao: podeGerenciarAvisos } = usePermissoes('configuracoes', 'gerenciar_avisos');
  const { temPermissao: podeGerenciarConferentes } = usePermissoes('configuracoes', 'gerenciar_conferentes');
  const { temPermissao: podeGerenciarMotivosOcorrencia } = usePermissoes('configuracoes', 'gerenciar_motivos_ocorrencia');

  const filiais = [
    { id: 'FILIAL_1', nome: 'Filial 1 - UP Distribuidora de Produtos e Descartáveis LTDA' },
    { id: 'FILIAL_2', nome: 'Filial 2 - Aleplast Comércio de Produtos e Descartáveis LTDA' },
    { id: 'FILIAL_3', nome: 'Filial 3 - Alepack Industria e Comercio Plasticos LTDA' }
  ];

  // Fallback para admin - acesso total a todas as configurações
  const isAdmin = user?.perfil === 'admin';
  const podeVerNotificacoes = isAdmin || podeGerenciarNotificacoes;
  const podeVerTermosRegras = isAdmin || podeGerenciarTermosRegras;
  const podeVerAvisos = isAdmin || podeGerenciarAvisos;
  const podeVerConferentes = isAdmin || podeGerenciarConferentes;
  const podeVerMotivosOcorrencia = isAdmin || podeGerenciarMotivosOcorrencia;
  const [abaAtiva, setAbaAtiva] = useState<'gerais' | 'cargas' | 'visitas'>('gerais');
  const [subAbaGeral, setSubAbaGeral] = useState<'notificacoes' | 'termos_regras' | 'avisos'>('notificacoes');
  const [subAbaCargas, setSubAbaCargas] = useState<'conferentes' | 'motivos'>('conferentes');
  const [subAbaVisitas, setSubAbaVisitas] = useState<'motivos_transferencia' | 'motivos_cancelamento' | 'motivos_reprovacao' | 'motivos_reagendamento' | 'motivos_finalizacao' | 'avaliacao' | 'pos_visita'>('motivos_transferencia');
  const [loading, setLoading] = useState(false);

  // Configurações
  const [configuracoes, setConfiguracoes] = useState<Configuracao[]>([]);
  const [notificacoesHabilitadas, setNotificacoesHabilitadas] = useState(true);
  const [tiposNotificacoes, setTiposNotificacoes] = useState('');
  const [termosRegrasTitulo, setTermosRegrasTitulo] = useState('');
  const [termosRegrasTexto, setTermosRegrasTexto] = useState('');
  const [postosFiscais, setPostosFiscais] = useState('');
  const [horarioFuncionamento, setHorarioFuncionamento] = useState('Segunda a Sexta: 08:00 às 18:00');
  const [horarioRecebimentoNF, setHorarioRecebimentoNF] = useState('Segunda a Sexta: 08:00 às 17:00');

  // Motivos de Ocorrência
  const [motivosOcorrencia, setMotivosOcorrencia] = useState<MotivoOcorrencia[]>([]);
  const [showModalMotivo, setShowModalMotivo] = useState(false);
  const [editandoMotivo, setEditandoMotivo] = useState<MotivoOcorrencia | null>(null);
  const [motivoNome, setMotivoNome] = useState('');
  const [motivoDescricao, setMotivoDescricao] = useState('');

  // Conferentes
  const [conferentes, setConferentes] = useState<Conferente[]>([]);
  const [showModalConferente, setShowModalConferente] = useState(false);
  const [editandoConferente, setEditandoConferente] = useState<Conferente | null>(null);
  const [conferenteNome, setConferenteNome] = useState('');
  const [conferenteFilial, setConferenteFilial] = useState('');

  // Gestão de Visitas - Motivos e Avaliação
  const [motivosTransferencia, setMotivosTransferencia] = useState<any[]>([]);
  const [motivosCancelamento, setMotivosCancelamento] = useState<any[]>([]);
  const [motivosReprovacao, setMotivosReprovacao] = useState<any[]>([]);
  const [motivosReagendamento, setMotivosReagendamento] = useState<any[]>([]);
  const [motivosFinalizacao, setMotivosFinalizacao] = useState<any[]>([]);
  const [perguntasAvaliacao, setPerguntasAvaliacao] = useState<any[]>([]);
  const [perguntasPosVisita, setPerguntasPosVisita] = useState<any[]>([]);
  const [novoMotivoVisita, setNovoMotivoVisita] = useState('');
  const [novaPerguntaAvaliacao, setNovaPerguntaAvaliacao] = useState('');
  const [novaPerguntaOrdem, setNovaPerguntaOrdem] = useState(0);
  const [novaPerguntaPosVisita, setNovaPerguntaPosVisita] = useState('');
  const [novaPerguntaPosVisitaTipo, setNovaPerguntaPosVisitaTipo] = useState<'estrelas' | 'sim_nao' | 'texto'>('estrelas');
  const [novaPerguntaPosVisitaOrdem, setNovaPerguntaPosVisitaOrdem] = useState(0);

  // Modal de Motivo de Visita
  const [showModalMotivoVisita, setShowModalMotivoVisita] = useState(false);
  const [motivoVisitaTipo, setMotivoVisitaTipo] = useState('');
  const [motivoVisitaTexto, setMotivoVisitaTexto] = useState('');
  const [motivoVisitaGravidade, setMotivoVisitaGravidade] = useState<'baixa' | 'media' | 'alta'>('media');

  // Avisos
  const [avisos, setAvisos] = useState<Aviso[]>([]);
  const [showModalAviso, setShowModalAviso] = useState(false);
  const [editandoAviso, setEditandoAviso] = useState<Aviso | null>(null);
  const [avisoTitulo, setAvisoTitulo] = useState('');
  const [avisoMensagem, setAvisoMensagem] = useState('');
  const [avisoLado, setAvisoLado] = useState<'esquerdo' | 'direito'>('esquerdo');
  const [avisoData, setAvisoData] = useState(new Date().toISOString().split('T')[0]);
  const [avisoStatus, setAvisoStatus] = useState<'ativo' | 'inativo'>('ativo');
  
  useEffect(() => {
    carregarConfiguracoes();
    carregarMotivosOcorrencia();
    carregarConferentes();
    carregarAvisos();
    carregarTermosRegras(filialAtual);
    carregarMotivosVisitas();
  }, [filialAtual]);

  const carregarMotivosVisitas = async () => {
    try {
      const [trans, canc, reprov, reag, final, aval, posVis] = await Promise.all([
        motivosTransferenciaService.listar(),
        motivosCancelamentoService.listar(),
        motivosReprovacaoService.listar(),
        motivosReagendamentoService.listar(),
        motivosFinalizacaoService.listar(),
        avaliacaoConfigService.listar(),
        posVisitaConfigService.listar()
      ]);
      setMotivosTransferencia(trans?.data || []);
      setMotivosCancelamento(canc?.data || []);
      setMotivosReprovacao(reprov?.data || []);
      setMotivosReagendamento(reag?.data || []);
      setMotivosFinalizacao(final?.data || []);
      setPerguntasAvaliacao(aval?.data || []);
      setPerguntasPosVisita(posVis?.data || []);
    } catch (error) {
      console.error('Erro ao carregar motivos de visitas:', error);
    }
  };

  const handleCriarMotivoVisita = async () => {
    if (!motivoVisitaTexto.trim()) return;
    try {
      switch (motivoVisitaTipo) {
        case 'transferencia': await motivosTransferenciaService.criar(motivoVisitaTexto, motivoVisitaGravidade); break;
        case 'cancelamento': await motivosCancelamentoService.criar(motivoVisitaTexto, motivoVisitaGravidade); break;
        case 'reprovacao': await motivosReprovacaoService.criar(motivoVisitaTexto, motivoVisitaGravidade); break;
        case 'reagendamento': await motivosReagendamentoService.criar(motivoVisitaTexto, motivoVisitaGravidade); break;
        case 'finalizacao': await motivosFinalizacaoService.criar(motivoVisitaTexto, motivoVisitaGravidade); break;
      }
      setMotivoVisitaTexto('');
      setMotivoVisitaGravidade('media');
      setShowModalMotivoVisita(false);
      carregarMotivosVisitas();
    } catch (error) {
      console.error('Erro ao criar motivo:', error);
      alert('Erro ao criar motivo');
    }
  };

  const abrirModalMotivoVisita = (tipo: string) => {
    setMotivoVisitaTipo(tipo);
    setMotivoVisitaTexto('');
    setMotivoVisitaGravidade('media');
    setShowModalMotivoVisita(true);
  };

  const gravidadeBadge = (gravidade?: string) => {
    if (!gravidade) return null;
    const colors: Record<string, string> = { baixa: 'bg-green-100 text-green-700', media: 'bg-yellow-100 text-yellow-700', alta: 'bg-red-100 text-red-700' };
    const labels: Record<string, string> = { baixa: 'Baixa', media: 'Média', alta: 'Alta' };
    return <span className={`text-xs px-2 py-0.5 rounded-full ${colors[gravidade] || colors.media}`}>{labels[gravidade] || gravidade}</span>;
  };

  const handleExcluirMotivoVisita = async (tipo: string, id: string) => {
    if (!window.confirm('Excluir este motivo?')) return;
    try {
      switch (tipo) {
        case 'transferencia': await motivosTransferenciaService.excluir(id); break;
        case 'cancelamento': await motivosCancelamentoService.excluir(id); break;
        case 'reprovacao': await motivosReprovacaoService.excluir(id); break;
        case 'reagendamento': await motivosReagendamentoService.excluir(id); break;
        case 'finalizacao': await motivosFinalizacaoService.excluir(id); break;
      }
      carregarMotivosVisitas();
    } catch (error) {
      console.error('Erro ao excluir motivo:', error);
      alert('Erro ao excluir motivo');
    }
  };

  const handleCriarPerguntaAvaliacao = async () => {
    if (!novaPerguntaAvaliacao.trim()) return;
    try {
      await avaliacaoConfigService.criar(novaPerguntaAvaliacao, novaPerguntaOrdem);
      setNovaPerguntaAvaliacao('');
      setNovaPerguntaOrdem(0);
      carregarMotivosVisitas();
    } catch (error) {
      console.error('Erro ao criar pergunta:', error);
      alert('Erro ao criar pergunta');
    }
  };

  const handleExcluirPerguntaAvaliacao = async (id: string) => {
    if (!window.confirm('Excluir esta pergunta?')) return;
    try {
      await avaliacaoConfigService.excluir(id);
      carregarMotivosVisitas();
    } catch (error) {
      console.error('Erro ao excluir pergunta:', error);
      alert('Erro ao excluir pergunta');
    }
  };

  const handleCriarPerguntaPosVisita = async () => {
    if (!novaPerguntaPosVisita.trim()) return;
    try {
      await posVisitaConfigService.criar(novaPerguntaPosVisita, novaPerguntaPosVisitaTipo, novaPerguntaPosVisitaOrdem);
      setNovaPerguntaPosVisita('');
      setNovaPerguntaPosVisitaTipo('estrelas');
      setNovaPerguntaPosVisitaOrdem(0);
      carregarMotivosVisitas();
    } catch (error) {
      console.error('Erro ao criar pergunta de pós visita:', error);
      alert('Erro ao criar pergunta');
    }
  };

  const handleExcluirPerguntaPosVisita = async (id: string) => {
    if (!window.confirm('Excluir esta pergunta?')) return;
    try {
      await posVisitaConfigService.excluir(id);
      carregarMotivosVisitas();
    } catch (error) {
      console.error('Erro ao excluir pergunta de pós visita:', error);
      alert('Erro ao excluir pergunta');
    }
  };

  const carregarConfiguracoes = async () => {
    try {
      setLoading(true);
      const dados = await ConfiguracoesService.buscarTodas();
      setConfiguracoes(dados);
      
      // Carregar valores específicos (globais, sem filial)
      const notifHabilitadas = dados.find(c => c.chave === 'notificacoes_habilitadas' && !c.filial);
      const notifTipos = dados.find(c => c.chave === 'notificacoes_tipos' && !c.filial);
      
      if (notifHabilitadas) setNotificacoesHabilitadas(notifHabilitadas.valor === 'true');
      if (notifTipos) setTiposNotificacoes(notifTipos.valor);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      alert('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const carregarTermosRegras = async (filial: string) => {
    try {
      const dados = await ConfiguracoesService.buscarTodas(filial);
      const termosTitulo = dados.find(c => c.chave === 'termos_regras_titulo');
      const termosTexto = dados.find(c => c.chave === 'termos_regras_texto');
      const postos = dados.find(c => c.chave === 'postos_fiscais');
      const horario = dados.find(c => c.chave === 'horario_atendimento');
      const horarioNF = dados.find(c => c.chave === 'horario_recebimento_nf');
      
      if (termosTitulo) setTermosRegrasTitulo(termosTitulo.valor);
      if (termosTexto) setTermosRegrasTexto(termosTexto.valor);
      if (postos) setPostosFiscais(postos.valor);
      if (horario) setHorarioFuncionamento(horario.valor);
      if (horarioNF) setHorarioRecebimentoNF(horarioNF.valor);
    } catch (error) {
      console.error('Erro ao carregar termos e regras:', error);
    }
  };

  const carregarMotivosOcorrencia = async () => {
    try {
      const dados = await MotivosOcorrenciaService.buscarTodos();
      setMotivosOcorrencia(dados);
    } catch (error) {
      console.error('Erro ao carregar motivos:', error);
    }
  };

  const carregarConferentes = async () => {
    try {
      const dados = await ConfiguracoesService.buscarConferentes();
      setConferentes(dados);
    } catch (error) {
      console.error('Erro ao carregar conferentes:', error);
    }
  };

  const carregarAvisos = async () => {
    try {
      // Usar a primeira filial do usuário ou FILIAL_1 como padrão
      const filial = user?.filiais?.[0] || 'FILIAL_1';
      const dados = await AvisosService.buscarTodas(filial);
      setAvisos(dados);
    } catch (error) {
      console.error('Erro ao carregar avisos:', error);
    }
  };

  const salvarConfiguracao = async (chave: string, valor: string) => {
    try {
      await ConfiguracoesService.atualizar(chave, valor);
      alert('Configuração salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      alert('Erro ao salvar configuração');
    }
  };

  const salvarNotificacoes = () => {
    salvarConfiguracao('notificacoes_habilitadas', notificacoesHabilitadas.toString());
    salvarConfiguracao('notificacoes_tipos', tiposNotificacoes);
  };

  const salvarTermosRegras = async () => {
    try {
      await ConfiguracoesService.atualizar('termos_regras_titulo', termosRegrasTitulo, filialAtual);
      await ConfiguracoesService.atualizar('termos_regras_texto', termosRegrasTexto, filialAtual);
      await ConfiguracoesService.atualizar('postos_fiscais', postosFiscais, filialAtual);
      await ConfiguracoesService.atualizar('horario_atendimento', horarioFuncionamento, filialAtual);
      await ConfiguracoesService.atualizar('horario_recebimento_nf', horarioRecebimentoNF, filialAtual);
      alert('Termos e regras salvos com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar termos e regras:', error);
      alert('Erro ao salvar termos e regras');
    }
  };

  const handleSalvarMotivo = async () => {
    try {
      if (editandoMotivo) {
        await MotivosOcorrenciaService.atualizar(editandoMotivo.id, {
          nome: motivoNome,
          descricao: motivoDescricao,
          ativo: true
        });
      } else {
        await MotivosOcorrenciaService.criar({
          nome: motivoNome,
          descricao: motivoDescricao,
          ativo: true
        });
      }
      
      setShowModalMotivo(false);
      setEditandoMotivo(null);
      setMotivoNome('');
      setMotivoDescricao('');
      carregarMotivosOcorrencia();
      alert('Motivo salvo com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar motivo:', error);
      alert('Erro ao salvar motivo');
    }
  };

  const handleEditarMotivo = (motivo: MotivoOcorrencia) => {
    setEditandoMotivo(motivo);
    setMotivoNome(motivo.nome);
    setMotivoDescricao(motivo.descricao || '');
    setShowModalMotivo(true);
  };

  const handleRemoverMotivo = async (id: string) => {
    if (!window.confirm('Deseja realmente remover este motivo?')) return;
    
    try {
      await MotivosOcorrenciaService.excluir(id);
      carregarMotivosOcorrencia();
      alert('Motivo removido com sucesso!');
    } catch (error) {
      console.error('Erro ao remover motivo:', error);
      alert('Erro ao remover motivo');
    }
  };

  const handleSalvarConferente = async () => {
    try {
      if (editandoConferente) {
        await ConfiguracoesService.atualizarConferente(
          editandoConferente.id,
          conferenteNome,
          conferenteFilial,
          true
        );
      } else {
        await ConfiguracoesService.criarConferente(conferenteNome, conferenteFilial);
      }
      
      setShowModalConferente(false);
      setEditandoConferente(null);
      setConferenteNome('');
      setConferenteFilial('');
      carregarConferentes();
      alert('Conferente salvo com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar conferente:', error);
      alert('Erro ao salvar conferente');
    }
  };

  const handleEditarConferente = (conferente: Conferente) => {
    setEditandoConferente(conferente);
    setConferenteNome(conferente.nome);
    setConferenteFilial(conferente.filial);
    setShowModalConferente(true);
  };

  const handleRemoverConferente = async (id: string) => {
    if (!window.confirm('Deseja realmente remover este conferente?')) return;

    try {
      await ConfiguracoesService.removerConferente(id);
      carregarConferentes();
      alert('Conferente removido com sucesso!');
    } catch (error) {
      console.error('Erro ao remover conferente:', error);
      alert('Erro ao remover conferente');
    }
  };

  const handleSalvarAviso = async () => {
    try {
      if (editandoAviso) {
        await AvisosService.atualizar(editandoAviso.id, {
          titulo: avisoTitulo,
          mensagem: avisoMensagem,
          lado: avisoLado,
          data: avisoData,
          status: avisoStatus
        });
      } else {
        await AvisosService.criar({
          titulo: avisoTitulo,
          mensagem: avisoMensagem,
          lado: avisoLado,
          data: avisoData,
          status: avisoStatus,
          filial: user?.filiais?.[0] || 'FILIAL_1'
        } as any);
      }

      setShowModalAviso(false);
      setEditandoAviso(null);
      setAvisoTitulo('');
      setAvisoMensagem('');
      setAvisoLado('esquerdo');
      setAvisoData(new Date().toISOString().split('T')[0]);
      setAvisoStatus('ativo');
      carregarAvisos();
      alert('Aviso salvo com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar aviso:', error);
      alert('Erro ao salvar aviso');
    }
  };

  const handleEditarAviso = (aviso: Aviso) => {
    setEditandoAviso(aviso);
    setAvisoTitulo(aviso.titulo);
    setAvisoMensagem(aviso.mensagem);
    setAvisoLado(aviso.lado);
    setAvisoData(aviso.data);
    setAvisoStatus(aviso.status);
    setShowModalAviso(true);
  };

  const handleRemoverAviso = async (id: string) => {
    if (!window.confirm('Deseja realmente remover este aviso?')) return;

    try {
      await AvisosService.deletar(id);
      carregarAvisos();
      alert('Aviso removido com sucesso!');
    } catch (error) {
      console.error('Erro ao remover aviso:', error);
      alert('Erro ao remover aviso');
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Configurações do Sistema</h1>
      
      {/* Abas de navegação principais */}
      <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 mb-6">
        <button
          onClick={() => setAbaAtiva('gerais')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            abaAtiva === 'gerais'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Configurações Gerais
        </button>
        <button
          onClick={() => setAbaAtiva('cargas')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            abaAtiva === 'cargas'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Cargas
        </button>
        <button
          onClick={() => setAbaAtiva('visitas')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            abaAtiva === 'visitas'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Gestão de Visitas
        </button>
      </div>

      {/* Subabas de Configurações Gerais */}
      {abaAtiva === 'gerais' && (
        <div className="flex space-x-1 bg-gray-50 rounded-lg p-1 mb-6">
          {podeVerNotificacoes && (
            <button
              onClick={() => setSubAbaGeral('notificacoes')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                subAbaGeral === 'notificacoes'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Notificações
            </button>
          )}
          {podeVerTermosRegras && (
            <button
              onClick={() => setSubAbaGeral('termos_regras')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                subAbaGeral === 'termos_regras'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Termos e Regras
            </button>
          )}
          {podeVerAvisos && (
            <button
              onClick={() => setSubAbaGeral('avisos')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                subAbaGeral === 'avisos'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Avisos e Lembretes
            </button>
          )}
        </div>
      )}

      {/* Subabas de Cargas */}
      {abaAtiva === 'cargas' && (
        <div className="flex space-x-1 bg-gray-50 rounded-lg p-1 mb-6">
          {podeVerConferentes && (
            <button
              onClick={() => setSubAbaCargas('conferentes')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                subAbaCargas === 'conferentes'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Conferentes
            </button>
          )}
          {podeVerMotivosOcorrencia && (
            <button
              onClick={() => setSubAbaCargas('motivos')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                subAbaCargas === 'motivos'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Motivos de Ocorrência
            </button>
          )}
        </div>
      )}

      {/* Subabas de Gestão de Visitas */}
      {abaAtiva === 'visitas' && (
        <div className="flex flex-wrap gap-1 bg-gray-50 rounded-lg p-1 mb-6">
          <button
            onClick={() => setSubAbaVisitas('motivos_transferencia')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${subAbaVisitas === 'motivos_transferencia' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          >
            Motivos de Transferência
          </button>
          <button
            onClick={() => setSubAbaVisitas('motivos_cancelamento')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${subAbaVisitas === 'motivos_cancelamento' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          >
            Motivos de Cancelamento
          </button>
          <button
            onClick={() => setSubAbaVisitas('motivos_reprovacao')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${subAbaVisitas === 'motivos_reprovacao' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          >
            Motivos de Reprovação
          </button>
          <button
            onClick={() => setSubAbaVisitas('motivos_reagendamento')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${subAbaVisitas === 'motivos_reagendamento' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          >
            Motivos de Reagendamento
          </button>
          <button
            onClick={() => setSubAbaVisitas('motivos_finalizacao')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${subAbaVisitas === 'motivos_finalizacao' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          >
            Motivos de Finalização
          </button>
          <button
            onClick={() => setSubAbaVisitas('avaliacao')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${subAbaVisitas === 'avaliacao' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          >
            Questionário de Avaliação
          </button>
          <button
            onClick={() => setSubAbaVisitas('pos_visita')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${subAbaVisitas === 'pos_visita' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
          >
            Pós Visita
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Carregando configurações...</p>
        </div>
      ) : (
        <>
          {/* Configurações de Notificações */}
          {abaAtiva === 'gerais' && subAbaGeral === 'notificacoes' && podeVerNotificacoes && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Configurações de Notificações</h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Habilitar Notificações
                    </label>
                    <p className="text-sm text-gray-500">
                      Habilitar ou desabilitar notificações do sistema
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notificacoesHabilitadas}
                      onChange={(e) => setNotificacoesHabilitadas(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipos de Notificações
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    Tipos de notificações habilitadas (separados por vírgula)
                  </p>
                  <textarea
                    value={tiposNotificacoes}
                    onChange={(e) => setTiposNotificacoes(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="solicitacao_aprovada,solicitacao_reprovada,pedido_digitado,carga_chegou"
                  />
                </div>

                <Button onClick={salvarNotificacoes} className="w-full">
                  Salvar Configurações de Notificações
                </Button>
              </div>
            </div>
          )}

          {/* Termos e Regras */}
          {abaAtiva === 'gerais' && subAbaGeral === 'termos_regras' && podeVerTermosRegras && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Termos e Regras por Filial</h2>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Selecione a Filial para Editar
                </label>
                <select
                  value={filialAtual}
                  onChange={(e) => setFilialAtual(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {filiais.map(filial => (
                    <option key={filial.id} value={filial.id}>
                      {filial.nome}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Título dos Termos e Regras
                  </label>
                  <input
                    type="text"
                    value={termosRegrasTitulo}
                    onChange={(e) => setTermosRegrasTitulo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Título que aparece no comprovante"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Texto dos Termos e Regras
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    Texto completo que aparece no comprovante de agendamento
                  </p>
                  <textarea
                    value={termosRegrasTexto}
                    onChange={(e) => setTermosRegrasTexto(e.target.value)}
                    rows={8}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Digite os termos e regras aqui..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Postos Fiscais Obrigatórios
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    Lista de postos fiscais obrigatórios (separados por ponto e vírgula)
                  </p>
                  <textarea
                    value={postosFiscais}
                    onChange={(e) => setPostosFiscais(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Delmiro Gouveia - Rodovia AL 423, KM 101;Novo Lino - Rodovia BR 101, KM 0;..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Horário de Funcionamento
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    Horário de funcionamento da filial (formato: Segunda a Sexta: 08:00 às 18:00)
                  </p>
                  <input
                    type="text"
                    value={horarioFuncionamento}
                    onChange={(e) => setHorarioFuncionamento(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Segunda a Sexta: 08:00 às 18:00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Horário de Recebimento de Notas Fiscais
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    Horário de recebimento de notas fiscais (formato: Segunda a Sexta: 08:00 às 17:00)
                  </p>
                  <input
                    type="text"
                    value={horarioRecebimentoNF}
                    onChange={(e) => setHorarioRecebimentoNF(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Segunda a Sexta: 08:00 às 17:00"
                  />
                </div>

                <Button onClick={salvarTermosRegras} className="w-full">
                  Salvar Termos e Regras
                </Button>
              </div>
            </div>
          )}

          {/* Motivos de Ocorrência */}
          {abaAtiva === 'cargas' && subAbaCargas === 'motivos' && podeVerMotivosOcorrencia && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Motivos de Ocorrência</h2>
                <Button onClick={() => {
                  setEditandoMotivo(null);
                  setMotivoNome('');
                  setMotivoDescricao('');
                  setShowModalMotivo(true);
                }}>
                  + Novo Motivo
                </Button>
              </div>
              
              <div className="space-y-2">
                {motivosOcorrencia.map((motivo) => (
                  <div key={motivo.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                    <div>
                      <h3 className="font-medium">{motivo.nome}</h3>
                      {motivo.descricao && (
                        <p className="text-sm text-gray-500">{motivo.descricao}</p>
                      )}
                      {!motivo.ativo && (
                        <span className="text-xs text-red-600">(Inativo)</span>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" onClick={() => handleEditarMotivo(motivo)}>
                        Editar
                      </Button>
                      <Button variant="error" size="sm" onClick={() => handleRemoverMotivo(motivo.id)}>
                        Remover
                      </Button>
                    </div>
                  </div>
                ))}
                {motivosOcorrencia.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    Nenhum motivo de ocorrência cadastrado
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Conferentes */}
          {abaAtiva === 'cargas' && subAbaCargas === 'conferentes' && podeVerConferentes && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Conferentes</h2>
                <Button onClick={() => {
                  setEditandoConferente(null);
                  setConferenteNome('');
                  setConferenteFilial('');
                  setShowModalConferente(true);
                }}>
                  + Novo Conferente
                </Button>
              </div>

              <div className="space-y-2">
                {conferentes.map((conferente) => (
                  <div key={conferente.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                    <div>
                      <h3 className="font-medium">{conferente.nome}</h3>
                      <p className="text-sm text-gray-500">Filial: {conferente.filial}</p>
                      {!conferente.ativo && (
                        <span className="text-xs text-red-600">(Inativo)</span>
                      )}
                    </div>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" onClick={() => handleEditarConferente(conferente)}>
                        Editar
                      </Button>
                      <Button variant="error" size="sm" onClick={() => handleRemoverConferente(conferente.id)}>
                        Remover
                      </Button>
                    </div>
                  </div>
                ))}
                {conferentes.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    Nenhum conferente cadastrado
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Avisos e Lembretes */}
          {abaAtiva === 'gerais' && subAbaGeral === 'avisos' && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Avisos e Lembretes</h2>
                <Button onClick={() => {
                  setEditandoAviso(null);
                  setAvisoTitulo('');
                  setAvisoMensagem('');
                  setAvisoLado('esquerdo');
                  setAvisoData(new Date().toISOString().split('T')[0]);
                  setAvisoStatus('ativo');
                  setShowModalAviso(true);
                }}>
                  + Novo Aviso
                </Button>
              </div>

              <div className="space-y-2">
                {avisos.map((aviso) => (
                  <div key={aviso.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                    <div>
                      <h3 className="font-medium">{aviso.titulo}</h3>
                      <p className="text-sm text-gray-500">{aviso.mensagem}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-400">Lado: {aviso.lado}</span>
                        <span className="text-xs text-gray-400">Data: {aviso.data}</span>
                        {aviso.status === 'inativo' && (
                          <span className="text-xs text-red-600">(Inativo)</span>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm" onClick={() => handleEditarAviso(aviso)}>
                        Editar
                      </Button>
                      <Button variant="error" size="sm" onClick={() => handleRemoverAviso(aviso.id)}>
                        Remover
                      </Button>
                    </div>
                  </div>
                ))}
                {avisos.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    Nenhum aviso cadastrado
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* ===================== GESTÃO DE VISITAS ===================== */}

      {/* Motivos de Transferência */}
      {abaAtiva === 'visitas' && subAbaVisitas === 'motivos_transferencia' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Motivos de Transferência</h2>
          </div>
          <div className="mb-4">
            <Button onClick={() => abrirModalMotivoVisita('transferencia')}>+ Adicionar Motivo</Button>
          </div>
          <div className="space-y-2">
            {motivosTransferencia.map((m) => (
              <div key={m.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{m.motivo}</span>
                  {gravidadeBadge(m.gravidade)}
                </div>
                <button onClick={() => handleExcluirMotivoVisita('transferencia', m.id)} className="text-red-600 hover:text-red-800 text-sm">Excluir</button>
              </div>
            ))}
            {motivosTransferencia.length === 0 && <p className="text-gray-500 text-sm">Nenhum motivo cadastrado.</p>}
          </div>
        </div>
      )}

      {/* Motivos de Cancelamento */}
      {abaAtiva === 'visitas' && subAbaVisitas === 'motivos_cancelamento' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Motivos de Cancelamento</h2>
          </div>
          <div className="mb-4">
            <Button onClick={() => abrirModalMotivoVisita('cancelamento')}>+ Adicionar Motivo</Button>
          </div>
          <div className="space-y-2">
            {motivosCancelamento.map((m) => (
              <div key={m.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{m.motivo}</span>
                  {gravidadeBadge(m.gravidade)}
                </div>
                <button onClick={() => handleExcluirMotivoVisita('cancelamento', m.id)} className="text-red-600 hover:text-red-800 text-sm">Excluir</button>
              </div>
            ))}
            {motivosCancelamento.length === 0 && <p className="text-gray-500 text-sm">Nenhum motivo cadastrado.</p>}
          </div>
        </div>
      )}

      {/* Motivos de Reprovação */}
      {abaAtiva === 'visitas' && subAbaVisitas === 'motivos_reprovacao' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Motivos de Reprovação</h2>
          </div>
          <div className="mb-4">
            <Button onClick={() => abrirModalMotivoVisita('reprovacao')}>+ Adicionar Motivo</Button>
          </div>
          <div className="space-y-2">
            {motivosReprovacao.map((m) => (
              <div key={m.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{m.motivo}</span>
                  {gravidadeBadge(m.gravidade)}
                </div>
                <button onClick={() => handleExcluirMotivoVisita('reprovacao', m.id)} className="text-red-600 hover:text-red-800 text-sm">Excluir</button>
              </div>
            ))}
            {motivosReprovacao.length === 0 && <p className="text-gray-500 text-sm">Nenhum motivo cadastrado.</p>}
          </div>
        </div>
      )}

      {/* Motivos de Reagendamento */}
      {abaAtiva === 'visitas' && subAbaVisitas === 'motivos_reagendamento' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Motivos de Reagendamento</h2>
          </div>
          <div className="mb-4">
            <Button onClick={() => abrirModalMotivoVisita('reagendamento')}>+ Adicionar Motivo</Button>
          </div>
          <div className="space-y-2">
            {motivosReagendamento.map((m) => (
              <div key={m.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{m.motivo}</span>
                  {gravidadeBadge(m.gravidade)}
                </div>
                <button onClick={() => handleExcluirMotivoVisita('reagendamento', m.id)} className="text-red-600 hover:text-red-800 text-sm">Excluir</button>
              </div>
            ))}
            {motivosReagendamento.length === 0 && <p className="text-gray-500 text-sm">Nenhum motivo cadastrado.</p>}
          </div>
        </div>
      )}

      {/* Motivos de Finalização */}
      {abaAtiva === 'visitas' && subAbaVisitas === 'motivos_finalizacao' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Motivos de Finalização de Atendimento</h2>
          </div>
          <div className="mb-4">
            <Button onClick={() => abrirModalMotivoVisita('finalizacao')}>+ Adicionar Motivo</Button>
          </div>
          <div className="space-y-2">
            {motivosFinalizacao.map((m) => (
              <div key={m.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{m.motivo}</span>
                  {gravidadeBadge(m.gravidade)}
                </div>
                <button onClick={() => handleExcluirMotivoVisita('finalizacao', m.id)} className="text-red-600 hover:text-red-800 text-sm">Excluir</button>
              </div>
            ))}
            {motivosFinalizacao.length === 0 && <p className="text-gray-500 text-sm">Nenhum motivo cadastrado.</p>}
          </div>
        </div>
      )}

      {/* Questionário de Avaliação */}
      {abaAtiva === 'visitas' && subAbaVisitas === 'avaliacao' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Questionário de Avaliação de Atendimento</h2>
          </div>
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={novaPerguntaAvaliacao}
              onChange={(e) => setNovaPerguntaAvaliacao(e.target.value)}
              placeholder="Nova pergunta..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="number"
              value={novaPerguntaOrdem}
              onChange={(e) => setNovaPerguntaOrdem(Number(e.target.value))}
              placeholder="Ordem"
              className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Button onClick={handleCriarPerguntaAvaliacao}>Adicionar</Button>
          </div>
          <div className="space-y-2">
            {perguntasAvaliacao.map((p) => (
              <div key={p.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <span className="text-sm font-medium">{p.pergunta}</span>
                  <span className="text-xs text-gray-500 ml-2">(Ordem: {p.ordem})</span>
                </div>
                <button onClick={() => handleExcluirPerguntaAvaliacao(p.id)} className="text-red-600 hover:text-red-800 text-sm">Excluir</button>
              </div>
            ))}
            {perguntasAvaliacao.length === 0 && <p className="text-gray-500 text-sm">Nenhuma pergunta cadastrada.</p>}
          </div>
        </div>
      )}

      {/* Pós Visita */}
      {abaAtiva === 'visitas' && subAbaVisitas === 'pos_visita' && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Questionário de Pós Visita</h2>
            <p className="text-sm text-gray-500">Exibido ao solicitante após a visita ser finalizada</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <input
              type="text"
              value={novaPerguntaPosVisita}
              onChange={(e) => setNovaPerguntaPosVisita(e.target.value)}
              placeholder="Nova pergunta..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={novaPerguntaPosVisitaTipo}
              onChange={(e) => setNovaPerguntaPosVisitaTipo(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="estrelas">Estrelas (0-5)</option>
              <option value="sim_nao">Sim / Não</option>
              <option value="texto">Texto livre</option>
            </select>
            <input
              type="number"
              value={novaPerguntaPosVisitaOrdem}
              onChange={(e) => setNovaPerguntaPosVisitaOrdem(Number(e.target.value))}
              placeholder="Ordem"
              className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Button onClick={handleCriarPerguntaPosVisita}>Adicionar</Button>
          </div>
          <div className="space-y-2">
            {perguntasPosVisita.map((p) => (
              <div key={p.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium">{p.pergunta}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    p.tipo === 'estrelas' ? 'bg-yellow-100 text-yellow-700' :
                    p.tipo === 'sim_nao' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {p.tipo === 'estrelas' ? '⭐ Estrelas' : p.tipo === 'sim_nao' ? '✓ Sim/Não' : '✍ Texto'}
                  </span>
                  <span className="text-xs text-gray-500">(Ordem: {p.ordem})</span>
                </div>
                <button onClick={() => handleExcluirPerguntaPosVisita(p.id)} className="text-red-600 hover:text-red-800 text-sm">Excluir</button>
              </div>
            ))}
            {perguntasPosVisita.length === 0 && <p className="text-gray-500 text-sm">Nenhuma pergunta cadastrada.</p>}
          </div>
        </div>
      )}

      {/* Modal de Motivo */}
      <Modal
        isOpen={showModalMotivo}
        onClose={() => {
          setShowModalMotivo(false);
          setEditandoMotivo(null);
          setMotivoNome('');
          setMotivoDescricao('');
        }}
        title={editandoMotivo ? 'Editar Motivo' : 'Novo Motivo'}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome do Motivo
            </label>
            <input
              type="text"
              value={motivoNome}
              onChange={(e) => setMotivoNome(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: Atraso na entrega"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrição (opcional)
            </label>
            <textarea
              value={motivoDescricao}
              onChange={(e) => setMotivoDescricao(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Descrição detalhada do motivo"
            />
          </div>
          <Button onClick={handleSalvarMotivo} className="w-full">
            {editandoMotivo ? 'Atualizar' : 'Criar'} Motivo
          </Button>
        </div>
      </Modal>

      {/* Modal de Conferente */}
      <Modal
        isOpen={showModalConferente}
        onClose={() => {
          setShowModalConferente(false);
          setEditandoConferente(null);
          setConferenteNome('');
          setConferenteFilial('');
        }}
        title={editandoConferente ? 'Editar Conferente' : 'Novo Conferente'}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome do Conferente
            </label>
            <input
              type="text"
              value={conferenteNome}
              onChange={(e) => setConferenteNome(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nome completo"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filial
            </label>
            <select
              value={conferenteFilial}
              onChange={(e) => setConferenteFilial(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Selecione uma filial</option>
              <option value="FILIAL_1">FILIAL_1</option>
              <option value="FILIAL_2">FILIAL_2</option>
              <option value="FILIAL_3">FILIAL_3</option>
            </select>
          </div>
          <Button onClick={handleSalvarConferente} className="w-full">
            {editandoConferente ? 'Atualizar' : 'Criar'} Conferente
          </Button>
        </div>
      </Modal>

      {/* Modal de Aviso */}
      <Modal
        isOpen={showModalAviso}
        onClose={() => {
          setShowModalAviso(false);
          setEditandoAviso(null);
          setAvisoTitulo('');
          setAvisoMensagem('');
          setAvisoLado('esquerdo');
          setAvisoData(new Date().toISOString().split('T')[0]);
          setAvisoStatus('ativo');
        }}
        title={editandoAviso ? 'Editar Aviso' : 'Novo Aviso'}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Título do Aviso
            </label>
            <input
              type="text"
              value={avisoTitulo}
              onChange={(e) => setAvisoTitulo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: Feriado"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mensagem
            </label>
            <textarea
              value={avisoMensagem}
              onChange={(e) => setAvisoMensagem(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Descrição do aviso"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Lado
            </label>
            <select
              value={avisoLado}
              onChange={(e) => setAvisoLado(e.target.value as 'esquerdo' | 'direito')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="esquerdo">Esquerdo (Avisos)</option>
              <option value="direito">Direito (Lembretes)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data
            </label>
            <input
              type="date"
              value={avisoData}
              onChange={(e) => setAvisoData(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={avisoStatus}
              onChange={(e) => setAvisoStatus(e.target.value as 'ativo' | 'inativo')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
            </select>
          </div>
          <Button onClick={handleSalvarAviso} className="w-full">
            {editandoAviso ? 'Atualizar' : 'Criar'} Aviso
          </Button>
        </div>
      </Modal>

      {/* Modal de Motivo de Visita (com gravidade) */}
      <Modal
        isOpen={showModalMotivoVisita}
        onClose={() => {
          setShowModalMotivoVisita(false);
          setMotivoVisitaTexto('');
          setMotivoVisitaGravidade('media');
        }}
        title="Novo Motivo"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Motivo *
            </label>
            <input
              type="text"
              value={motivoVisitaTexto}
              onChange={(e) => setMotivoVisitaTexto(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: Atraso na entrega"
              onKeyDown={(e) => { if (e.key === 'Enter' && motivoVisitaTexto.trim()) handleCriarMotivoVisita(); }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Gravidade
            </label>
            <div className="flex gap-2">
              {(['baixa', 'media', 'alta'] as const).map(g => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setMotivoVisitaGravidade(g)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    motivoVisitaGravidade === g
                      ? g === 'baixa' ? 'bg-green-500 text-white' : g === 'media' ? 'bg-yellow-500 text-white' : 'bg-red-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {g === 'baixa' ? 'Baixa' : g === 'media' ? 'Média' : 'Alta'}
                </button>
              ))}
            </div>
          </div>
          <Button onClick={handleCriarMotivoVisita} className="w-full">
            Criar Motivo
          </Button>
        </div>
      </Modal>
    </div>
  );
};

const ConfiguracoesProtegido = () => (
  <AbaProtegida nomeAba="Configurações">
    <Configuracoes />
  </AbaProtegida>
);

export default ConfiguracoesProtegido;
